import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { usePatchCompletedTaskMutation } from 'API/order/OrderTaskAPI';
import { Formik, Form } from 'formik';
import React from 'react';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import { IOrderTaskData, IUpdateCompletedOrderTaskRequest } from 'types/tasks';
import { Spinner } from 'ui-toolkit';
import { assertDefined } from 'utilities/Assertions';
import { EditTaskSchema } from 'validation/order/TaskSchema';
import { combineDateAndTime, IEditField } from './formTools';
import { UUID } from 'types/shared/UUID';
import { getDateFromTimeStamp, getTime, toLocalTimeZone } from 'utilities/timeHelpers';

export interface ICompletedTaskEditFormDisplayProps {
  task: IOrderTaskData;
  setIsEditing?: () => void;
}

interface ICompletedTaskEditFormProps {
  task: IOrderTaskData;
  form?: React.FC<IEditField>;
  display: React.FC<ICompletedTaskEditFormDisplayProps>;
}

export const CompletedTaskEditForm: React.FC<ICompletedTaskEditFormProps> = ({
  form: FormFields,
  task,
  display: Display,
}) => {
  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId);
  const [isEditing, setIsEditing] = React.useState(false);

  const [patchTask] = usePatchCompletedTaskMutation();
  return (
    <>
      {!isEditing && <Display task={task} setIsEditing={FormFields ? () => setIsEditing(true) : undefined} />}
      {isEditing && (
        <Formik
          enableReinitialize
          validationSchema={EditTaskSchema}
          initialValues={taskToEditCompletedTaskFormData(task)}
          isInitialValid={EditTaskSchema.isValidSync(task)}
          onSubmit={async (values) => {
            const body = convertEditDataToIUpdateCompletedOrderTaskRequest(values);
            const result = await patchTask({ orderId, body });
            if (checkForMutationSuccess(result, 'updating task', 'Error updating the task.')) {
              setIsEditing(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form>
              {isSubmitting && <Spinner />}
              {!isSubmitting && FormFields && <FormFields submitOnChange cancel={() => setIsEditing(false)} />}
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export interface IEditCompletedTaskFormData {
  id: UUID;
  description?: string;
  role?: string;
  completedAtDate?: string | null;
  completedAtTime?: string | null;
  completedBy?: string;
}

export const convertEditDataToIUpdateCompletedOrderTaskRequest = (
  editData: IEditCompletedTaskFormData
): IUpdateCompletedOrderTaskRequest => {
  const { id, completedBy, completedAtDate, completedAtTime } = editData;
  const completedAt = combineDateAndTime(completedAtDate, completedAtTime);
  const updateRequest: IUpdateCompletedOrderTaskRequest = {
    id,
    completedAt,
    completedBy,
  };
  return updateRequest;
};

const taskToEditCompletedTaskFormData = (task: IOrderTaskData): IEditCompletedTaskFormData => {
  const completedAtDate = task.completedAt ? getDateFromTimeStamp(task.completedAt) : undefined;
  const completedAtTime = task.completedAt ? getTime(toLocalTimeZone(task.completedAt)) : undefined;
  return {
    id: task.id!,
    description: task.description,
    role: task.role,
    completedAtDate,
    completedAtTime,
    completedBy: task.completedBy?.id,
  };
};
